import { KeySet } from '@tableau/data-structures';
import {
  ActivitySpinnerSize,
  ActivitySpinnerWidget,
  ButtonProps,
  ButtonType,
  DialogFooterButtonsWidget,
  TabStyles,
  TextAreaWidget,
  TextFieldProps,
  TextFieldWidget
} from '@tableau/widgets';
import * as React from 'react';
import { classes } from 'typestyle';
import { Formatters, Messages } from '../../../../gen-src/Localize';

import { assertUnreachable } from '../../utils/TypescriptHelpers';
import { ComposeEmailDialogStyles } from './ComposeEmailDialogStyles';

export interface ComposeEmailDialogFooterProps {
  readonly dismissDialog: () => void;
  readonly invokeSubmit: () => void;
  readonly isSubmitting: boolean;
  readonly isValid: boolean;
}

export type TextValidity = 'overflow' | 'empty' | 'valid' | 'initial';

/**
 * current design calls for no red border after editing, so 'empty' is just grey (but submit is still disabled)
 * current design calls for no green border, so 'valid' is just grey
 */
function isTextValid(validity: TextValidity) {
  switch (validity) {
    case 'overflow': return false;
    case 'empty': return undefined;
    case 'valid': return undefined;
    case 'initial': return undefined;
    default:
      assertUnreachable(validity);
      return undefined;
  }
}

function getSubjectMessage(props: ComposeEmailDialogContentProps) {
  if (props.subjectValidity === 'overflow') {
    return Messages.CharacterLimitExceeded({
      total: Formatters.formatCount(props.subjectText.length),
      maximum: Formatters.formatCount(ComposeEmailDialogStyles.MAXIMUM_SUBJECT_LENGTH)
    });
  }

  return Messages.CharacterCounter({
    characters: Formatters.formatCount(ComposeEmailDialogStyles.MAXIMUM_SUBJECT_LENGTH - props.subjectText.length)
  });
}

function getBodyMessage(props: ComposeEmailDialogContentProps) {
  if (props.bodyValidity === 'overflow') {
    return Messages.CharacterLimitExceeded({
      total: Formatters.formatCount(props.bodyText.length),
      maximum: Formatters.formatCount(ComposeEmailDialogStyles.MAXIMUM_BODY_LENGTH)
    });
  }

  return Messages.CharacterCounter({
    characters: Formatters.formatCount(ComposeEmailDialogStyles.MAXIMUM_BODY_LENGTH - props.bodyText.length)
  });
}

export function ComposeEmailDialogFooter(props: ComposeEmailDialogFooterProps) {
  const cancelButtonProps: ButtonProps = {
    testId: ComposeEmailDialogStyles.TEST_ID('cancel-button'),
    handleClick: props.dismissDialog,
    buttonType: ButtonType.Outline,
    children: Messages.Cancel(),
    disabled: props.isSubmitting,
  };

  const submitButtonProps: ButtonProps = {
    testId: ComposeEmailDialogStyles.TEST_ID('send-button'),
    handleClick: props.invokeSubmit,
    buttonType: ButtonType.Primary,
    children: Messages.Send(),
    disabled: props.isSubmitting || !props.isValid,
    style: props.isSubmitting ? ComposeEmailDialogStyles.buttonStyleSubmitting : {},
    // eslint-disable-next-line react/display-name
    getContent: () => {
      return (props.isSubmitting ? (
        <div style={{ marginTop: TabStyles.Spacing.SmallSpace }}>
          <ActivitySpinnerWidget spinnerSize={ActivitySpinnerSize.Small} testId='' />
        </div>
      ) :
        <span>{Messages.Send()}</span>
      );
    }
  };

  return (
    <DialogFooterButtonsWidget
      testId={ComposeEmailDialogStyles.TEST_ID('footer-buttons')}
      buttons={[
        cancelButtonProps,
        submitButtonProps,
      ]}
    />
  );
}

export interface ComposeEmailDialogContentProps {
  readonly subjectValidity: TextValidity;
  readonly bodyValidity: TextValidity;
  readonly subjectText: string;
  readonly bodyText: string;
  readonly selectedUserIds: KeySet;
  readonly handleSubjectChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  readonly handleBodyChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
}

export function ComposeEmailDialogContent(props: ComposeEmailDialogContentProps) {

  const subjectFieldProps: TextFieldProps = {
    kind: 'outline',
    label: Messages.EmailSubjectLabel(),
    testId: ComposeEmailDialogStyles.TEST_ID('subject-text-field')
  };

  const userCount = Object.keys(props.selectedUserIds).length;

  return (
    <>
      <ComposeEmailDialogStyles.LabelTextDiv style={{ paddingTop: 0 }}>
        {
          Messages.To()
        }
      </ComposeEmailDialogStyles.LabelTextDiv>
      <ComposeEmailDialogStyles.RecipientsDiv>
        {
          // TODO: this needs to get turned into pills of user names
          Messages.NumberOfRecipients({
            USER_COUNT: userCount,
            USER_COUNT_LOC: Formatters.formatCount(userCount)
          })
        }
      </ComposeEmailDialogStyles.RecipientsDiv>
      <TextFieldWidget
        className={classes(ComposeEmailDialogStyles.subjectContainer)}
        message={getSubjectMessage(props)}
        onChange={props.handleSubjectChange}
        placeholder={Messages.EmailSubjectLabel()}
        style={ComposeEmailDialogStyles.subjectStyle}
        valid={isTextValid(props.subjectValidity)}
        value={props.subjectText}
        {...subjectFieldProps}
      />

      <TextAreaWidget
        label={(
          <ComposeEmailDialogStyles.LabelTextDiv>
            {
              Messages.EmailMessageLabel()
            }
          </ComposeEmailDialogStyles.LabelTextDiv>
        )}
        onChange={props.handleBodyChange}
        className={ComposeEmailDialogStyles.messageAreaClass}
        testId={ComposeEmailDialogStyles.TEST_ID('text-area')}
        style={ComposeEmailDialogStyles.messageAreaStyle}
        valid={isTextValid(props.bodyValidity)}
        message={getBodyMessage(props)}
      />
    </>
  );
}
